import type { ReactElement } from 'react';
import { cn } from '@/lib/utils';
import type { AlertDescriptionSections } from '@kirin/api-client/helpers/alerts';
import { HiddenCharacterExcerpt } from './HiddenCharacterExcerpt';

interface AlertDescriptionProps {
  sections: AlertDescriptionSections | null;
  fallbackDescription: string;
  /**
   * KIR-4229: the content around an invisible-unicode hit, with each run of
   * invisible code points replaced by a marker the renderer highlights. Passed
   * only when Detection Logic is off; with it on that section owns the excerpt.
   */
  surroundingText?: string;
  className?: string;
}

/** The excerpt block, shared by the fallback and sectioned layouts. */
function SurroundingText({ value }: { value: string }): ReactElement {
  return (
    <div>
      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        Surrounding text
      </p>
      <HiddenCharacterExcerpt
        value={value}
        className="block"
        data-testid="alert-surrounding-text"
      />
    </div>
  );
}

export function AlertDescription({
  sections,
  fallbackDescription,
  surroundingText,
  className,
}: AlertDescriptionProps): ReactElement {
  const excerpt = surroundingText?.trim() ?? '';

  const whatHappened = sections?.whatHappened.trim() ?? '';
  const whyItMatters = sections?.whyItMatters.trim() ?? '';
  const detected = sections?.detected?.trim() ?? '';

  // No structured copy to show — the raw description carries the alert.
  if (whatHappened.length === 0 && whyItMatters.length === 0) {
    // Bare paragraph without an excerpt, so existing cards render the markup
    // they did before KIR-4229.
    if (excerpt.length === 0) {
      return (
        <p className={cn('break-words', className)}>{fallbackDescription}</p>
      );
    }
    return (
      <div className={cn('space-y-2', className)}>
        <p className="break-words">{fallbackDescription}</p>
        <SurroundingText value={excerpt} />
      </div>
    );
  }

  return (
    <div className={cn('space-y-2', className)}>
      {whatHappened.length > 0 && (
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            What happened
          </p>
          <p className="break-words">{whatHappened}</p>
        </div>
      )}
      {detected.length > 0 && (
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Detected
          </p>
          <p className="break-words font-mono" data-testid="alert-detected">
            {detected}
          </p>
        </div>
      )}
      {excerpt.length > 0 && <SurroundingText value={excerpt} />}
      {whyItMatters.length > 0 && (
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Why it matters
          </p>
          <p className="break-words">{whyItMatters}</p>
        </div>
      )}
    </div>
  );
}

AlertDescription.displayName = 'AlertDescription';
